import { Add, Close } from '@mui/icons-material'
import { Autocomplete, Box, Button, IconButton, TextField, Typography, useTheme } from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateLocalityCache } from 'src/api/localities'
import {
  invalidateMainComponentCache,
  useBulkUpdateMainComponents,
  type IBulkUpdateMainComponentsRequest,
} from 'src/api/main-component'
import { useGetMaintenanceTemplates } from 'src/api/main-component-type'
import { useGetCustomerSuppliers } from 'src/api/supplier'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { APIResultCount } from 'src/constant'
import type { IMainComponent } from 'src/generated-types/main-component'
import type { ISimpleMaintenanceTemplate } from 'src/generated-types/simple-maintenance-template'
import type { ICustomerAdminSupplier } from 'src/generated-types/supplier'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

export const BULK_EDIT_FIELD_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'orderNumber', labelKey: 'orderNumber' },
  { value: 'reference', labelKey: 'reference' },
  { value: 'description', labelKey: 'description' },
  { value: 'solidity', labelKey: 'solidity' },
  { value: 'lifetime', labelKey: 'lifetime' },
  { value: 'circumference', labelKey: 'circumference' },
  { value: 'supplierId', labelKey: 'supplier' },
  { value: 'productionYear', labelKey: 'productionYear' },
  { value: 'productionMonth', labelKey: 'productionMonth' },
]

export interface BulkEditEquipmentModalProps {
  selectedEquipments: IMainComponent[]
  customerId: EntityId
  pop: () => void
}

interface FieldValueRow {
  id: string
  fieldKey: string
  value: string
}

let rowIdCounter = 0
const newRow = (): FieldValueRow => ({
  id: `row-${++rowIdCounter}`,
  fieldKey: '',
  value: '',
})

const NUMERIC_FIELD_KEYS = ['solidity', 'lifetime', 'circumference', 'supplierId', 'productionMonth'] as const

export const BulkEditEquipmentModal: FC<BulkEditEquipmentModalProps> = ({ selectedEquipments, customerId, pop }) => {
  const { t } = useTranslation(['main-component', 'common'])
  const theme = useTheme()

  const [rows, setRows] = useState<FieldValueRow[]>(() => [newRow()])

  const commonTypeId = useMemo(() => {
    if (!selectedEquipments?.length) return null
    const first = selectedEquipments[0].typeId
    const allSame = selectedEquipments.every(e => e.typeId === first)
    return allSame ? first : null
  }, [selectedEquipments])

  const { mutateAsync: bulkUpdate, isLoading: submitting } = useBulkUpdateMainComponents(customerId as EntityId)
  const { data: suppliersData } = useGetCustomerSuppliers(customerId as EntityId, 0, APIResultCount.Max)
  const { data: maintenanceTemplates = [] } = useGetMaintenanceTemplates(
    customerId as EntityId,
    (commonTypeId ?? 0) as EntityId,
    false,
  )
  const suppliers: ICustomerAdminSupplier[] = suppliersData?.items ?? []

  const fieldOptions = useMemo(() => {
    const options = BULK_EDIT_FIELD_OPTIONS.map(opt => ({
      value: opt.value,
      label: t(opt.labelKey, { ns: 'main-component', defaultValue: opt.labelKey }),
    }))
    if (commonTypeId != null && commonTypeId !== 0) {
      options.push({
        value: 'maintenanceTemplateIds',
        label: t('maintenanceTemplate', { ns: 'main-component', defaultValue: 'Maintenance template' }),
      })
    }
    return options
  }, [t, commonTypeId])

  /** Production month is only selectable after the user has set a production year (another row). */
  const hasProductionYearWithValue = useMemo(
    () => rows.some(r => r.fieldKey === 'productionYear' && r.value.trim() !== ''),
    [rows],
  )

  const addRow = useCallback(() => {
    setRows(prev => [...prev, newRow()])
  }, [])

  const removeRow = useCallback((id: string) => {
    setRows(prev => (prev.length > 1 ? prev.filter(r => r.id !== id) : prev))
  }, [])

  const updateRow = useCallback((id: string, updates: Partial<FieldValueRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...updates } : r)))
  }, [])

  const handleSubmit = useCallback(async () => {
    const filledRows = rows.filter(r => r.fieldKey && r.value.trim() !== '')
    if (filledRows.length === 0) {
      toast.error(
        t('selectAtLeastOneFieldAndValue', {
          ns: 'main-component',
          defaultValue: 'Select at least one field and enter a value',
        }),
      )
      return
    }
    const hasProductionYearInPayload = filledRows.some(r => r.fieldKey === 'productionYear')
    if (filledRows.some(r => r.fieldKey === 'productionMonth') && !hasProductionYearInPayload) {
      toast.error(
        t('bulkEditProductionMonthRequiresYear', {
          ns: 'main-component',
          defaultValue: 'Set production year before production month.',
        }),
      )
      return
    }
    const updates: Record<string, string> = {}
    let maintenanceTemplateIdsFromRow: number[] | undefined
    filledRows.forEach(r => {
      if (r.fieldKey === 'maintenanceTemplateIds') {
        const ids = r.value
          .split(',')
          .map(s => Number(s.trim()))
          .filter(n => !Number.isNaN(n) && n > 0)
        if (ids.length > 0) maintenanceTemplateIdsFromRow = ids
      } else {
        updates[r.fieldKey] = r.value.trim()
      }
    })

    const numericFields = ['solidity', 'lifetime', 'circumference', 'supplierId', 'productionMonth'] as const
    for (const key of numericFields) {
      const raw = updates[key]
      if (raw != null) {
        const num = Number(raw)
        if (Number.isNaN(num) || !Number.isFinite(num)) {
          const fieldLabel = t(key, { ns: 'main-component' })
          toast.error(
            `${t('invalidNumber', { ns: 'main-component' })}: ${fieldLabel}`,
          )
          return
        }
        if (key === 'productionMonth' && (num < 0 || num > 11)) {
          toast.error(
            `${t('productionMonth', { ns: 'main-component' })} (${t('bulkEditProductionMonthHint', { ns: 'main-component' })})`,
          )
          return
        }
      }
    }

    const payload: IBulkUpdateMainComponentsRequest = {
      mainComponentIds: selectedEquipments.map(e => e.id as number),
      ...(updates.orderNumber != null && { orderNumber: updates.orderNumber }),
      ...(updates.reference != null && { reference: updates.reference }),
      ...(updates.description != null && { description: updates.description }),
      ...(updates.solidity != null && { solidity: Number(updates.solidity) }),
      ...(updates.lifetime != null && { lifetime: Number(updates.lifetime) }),
      ...(updates.circumference != null && { circumference: Number(updates.circumference) }),
      ...(updates.supplierId != null && { supplierId: Number(updates.supplierId) }),
      ...(updates.productionYear != null && { productionYear: updates.productionYear }),
      ...(updates.productionMonth != null && { productionMonth: Number(updates.productionMonth) }),
      ...(maintenanceTemplateIdsFromRow != null &&
        maintenanceTemplateIdsFromRow.length > 0 && { maintenanceTemplateIds: maintenanceTemplateIdsFromRow }),
    }
    await bulkUpdate(payload, {
      onSuccess: () => {
        invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
        invalidateLocalityCache.getCageStatus(customerId)
        invalidateLocalityCache.getLocality(customerId)

        toast.success(t('successfullyUpdated', { ns: 'common-api-message', defaultValue: 'Successfully updated' }))
        pop()
      },
      onError: error => {
        toast.error(t('updateFailed', { ns: 'common', defaultValue: 'Update failed' }))
      },
    })
  }, [rows, selectedEquipments, customerId, pop, t, bulkUpdate])

  const primaryColor = getThemeColor(theme, MUIThemeColors.primaryMain)
  const errorColor = theme.palette.error?.main ?? '#d32f2f'

  return (
    <GenericDialogLayout
      title={t('editEquipmentsDetails', { ns: 'main-component', defaultValue: 'Edit Equipments Details' })}
      pop={pop}
      titleSectionTopPadding="24px"
      titleSectionBottomPadding="10px"
      titleSectionXPadding="28px"
      closeIconSize="20px"
      fontWeight={600}
      removedCloseButton={false}
      actions={[
        <Button
          key="ok"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: primaryColor }, height: '44px', width: '100px' }}
        >
          {submitting
            ? t('saving', { ns: 'common', defaultValue: 'Saving...' })
            : t('ok', { ns: 'common', defaultValue: 'OK' })}
        </Button>,
      ]}
    >
      <Box sx={{ minWidth: 420 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, paddingTop: '10px' }}>
          {rows.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={() => removeRow(row.id)}
                sx={{ color: errorColor }}
                aria-label="remove row"
              >
                <Close fontSize="small" />
              </IconButton>
              <Autocomplete
                size="small"
                options={fieldOptions.filter(opt => {
                  if (opt.value === 'productionMonth' && !hasProductionYearWithValue && row.fieldKey !== 'productionMonth') {
                    return false
                  }
                  return (
                    opt.value === row.fieldKey ||
                    !rows.some(other => other.id !== row.id && other.fieldKey === opt.value)
                  )
                })}
                getOptionLabel={opt => (typeof opt === 'string' ? opt : opt?.label ?? '')}
                value={fieldOptions.find(o => o.value === row.fieldKey) ?? null}
                onChange={(_, opt) => updateRow(row.id, { fieldKey: opt?.value ?? '' })}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('field', { ns: 'main-component', defaultValue: 'Field' }) + ' *'}
                    placeholder={t('selectField', { ns: 'main-component', defaultValue: 'Select field...' })}
                  />
                )}
                sx={{ flex: 1, minWidth: 240, height: '40px' }}
              />
              <Typography component="span" sx={{ color: 'text.secondary', width: '60px' }} align="center">
                =
              </Typography>
              {row.fieldKey === 'supplierId' ? (
                <Autocomplete<ICustomerAdminSupplier>
                  size="small"
                  options={suppliers}
                  getOptionLabel={opt => opt?.name ?? ''}
                  value={suppliers.find(s => String(s.id) === row.value) ?? null}
                  onChange={(_, opt) => updateRow(row.id, { value: opt ? String(opt.id) : '' })}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('supplier', { ns: 'main-component', defaultValue: 'Supplier' })}
                      placeholder={t('selectSupplier', { ns: 'main-component', defaultValue: 'Select supplier...' })}
                    />
                  )}
                  sx={{ flex: 1, minWidth: 160 }}
                />
              ) : row.fieldKey === 'maintenanceTemplateIds' ? (
                <Autocomplete<ISimpleMaintenanceTemplate, true>
                  size="small"
                  multiple
                  disabled={commonTypeId == null || commonTypeId === 0}
                  options={maintenanceTemplates}
                  getOptionLabel={opt => opt?.name ?? ''}
                  value={
                    row.value
                      ? maintenanceTemplates.filter(t =>
                        row.value
                          .split(',')
                          .map(s => Number(s.trim()))
                          .includes(t.id),
                      )
                      : []
                  }
                  onChange={(_, opts) =>
                    updateRow(row.id, { value: opts?.length ? opts.map(t => t.id).join(',') : '' })
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('maintenance-template', { ns: 'maintenance', defaultValue: 'Maintenance template' })}
                      placeholder={
                        commonTypeId == null || commonTypeId === 0
                          ? t('selectSameTypeForMaintenanceTemplate', {
                            ns: 'main-component',
                            defaultValue: 'Select equipments of the same type',
                          })
                          : t('selectField', { ns: 'main-component', defaultValue: 'Select...' })
                      }
                    />
                  )}
                  sx={{ flex: 1, minWidth: 160 }}
                />
              ) : NUMERIC_FIELD_KEYS.includes(row.fieldKey as typeof NUMERIC_FIELD_KEYS[number]) ? (
                <TextField
                  size="small"
                  type="number"
                  label={t('value', { ns: 'main-component', defaultValue: 'Value' })}
                  value={row.value}
                  onChange={e => updateRow(row.id, { value: e.target.value })}
                  placeholder={
                    row.fieldKey === 'productionMonth'
                      ? t('bulkEditProductionMonthHint', {
                        ns: 'main-component'
                      })
                      : t('value', { ns: 'main-component', defaultValue: 'Value' })
                  }
                  inputProps={{
                    min: row.fieldKey === 'productionMonth' ? 0 : undefined,
                    max: row.fieldKey === 'productionMonth' ? 11 : undefined,
                    step: row.fieldKey === 'solidity' || row.fieldKey === 'circumference' ? 'any' : 1,
                  }}
                  sx={{ flex: 1, minWidth: 160 }}
                />
              ) : (
                <TextField
                  size="small"
                  label={t('value', { ns: 'main-component', defaultValue: 'Value' })}
                  value={row.value}
                  onChange={e => updateRow(row.id, { value: e.target.value })}
                  placeholder={t('value', { ns: 'main-component', defaultValue: 'Value' })}
                  sx={{ flex: 1, minWidth: 160 }}
                />
              )}
            </Box>
          ))}
        </Box>
        <Button
          startIcon={<Add />}
          onClick={addRow}
          sx={{
            color: primaryColor,
            mt: 1.5,
            textTransform: 'none',
            justifyContent: 'flex-start',
          }}
        >
          {t('addNew', { ns: 'main-component', defaultValue: 'Add new' })}
        </Button>
      </Box>
    </GenericDialogLayout>
  )
}
